import Component from '@glimmer/component'
import { isNone } from '@ember/utils'
import { get as getHelper } from '@ember/helper'
import { get } from '@ember/object'
import { or } from 'ember-truth-helpers'
import type { Config, DurationConfig } from 'teacher-ui-client/components/stable-table/basic-table'
import { formatValue, type FormatValueOptions } from 'teacher-ui-client/utils/format-value'
import { on } from '@ember/modifier'
import { optional } from 'teacher-ui-client/helpers/optional'
import type { ComponentLike } from '@glint/template'

export type RowId = string | number

export interface CustomCellSignature {
  Element: HTMLElement
  Args: { tableMeta: TableMeta; isHeader?: boolean }
}
type CustomCellComponent = ComponentLike<CustomCellSignature>

export interface ColumnObject {
  value?: unknown
  comparerKey?: string
  classNames?: string
  component?: CustomCellComponent
  componentClassNames?: string
  valuePath?: keyof RowObject
  cellClassNames?: string
  cellComponent?: CustomCellComponent
  cellComponentClassNames?: string
  formatEmptyConfig?: FormatValueOptions
  durationConfig?: DurationConfig
  sortKey?: keyof RowObject
  [key: string]: any
}

export interface CellObject {
  value?: RowObject[keyof RowObject]
  classNames?: string
  component?: CustomCellComponent
  componentClassNames?: string
  formatEmptyConfig?: FormatValueOptions
  durationConfig?: DurationConfig
  onClick?: () => any
  tooltipValue?: string
  adjacentIcon?: boolean
  [key: string]: any
}

export interface RowObject {
  classNames?: string
  cells?: CellObject[]
  id?: RowId
  [key: string]: any
}

export interface TableMeta {
  columnObject?: ColumnObject
  cellObject?: CellObject
  rowObject?: RowObject
  cellIndex?: number
  rowIndex?: number
  config?: Config
}

export function getCellValue(tableMeta: TableMeta) {
  const { rowObject: row, columnObject: column, cellObject: cell } = tableMeta

  const columnValuePath = column?.valuePath
  const cellValue = cell?.value as unknown
  const cellValueNone = isNone(cell?.value)

  if (row && cellValueNone && columnValuePath) {
    // Need to use some kind of getter here, since columnValuePath is often a nested set of keys.like.this
    return get(row, columnValuePath) as unknown
  }
  return cellValue
}

export function getCellFormatConfig(tableMeta: TableMeta) {
  return tableMeta.cellObject?.formatEmptyConfig ?? tableMeta.columnObject?.formatEmptyConfig
}

interface CellSignature {
  Element: HTMLTableCellElement
  Args: {
    columnObject: ColumnObject
    cellObject: CellObject
    rowObject: RowObject
    cellIndex: number
    rowIndex?: number
    config?: Config
    onClick?: () => void
  }
  Blocks: {
    default: [TableMeta]
  }
}

/**
 * Very similar to head-cell component, just using <td/> and taking into account anything defined on the column
 */
export class BasicTableCell extends Component<CellSignature> {
  get column() {
    return this.args.columnObject
  }

  get cell() {
    return this.args.cellObject
  }

  get cellValueNone() {
    return isNone(this.cell?.value)
  }

  get cellValueDefined() {
    return !this.cellValueNone
  }

  get formatEmptyConfig() {
    return this.cell?.formatEmptyConfig || this.column?.formatEmptyConfig
  }

  get tableMeta(): TableMeta {
    const { cellObject, columnObject, rowObject, cellIndex, rowIndex, config } = this.args
    return { cellObject, columnObject, rowObject, cellIndex, rowIndex, config }
  }

  <template>
    <td
      data-test-basic-table-cell={{@cellIndex}}
      class="{{@columnObject.cellClassNames}} {{@cellObject.classNames}}"
      role={{if @onClick "button"}}
      {{on "click" (optional @onClick)}}
      {{! This lint rule doesn't work too nicely with optional interactions }}
      {{! https://github.com/ember-template-lint/ember-template-lint/issues/274 }}
      {{! template-lint-disable no-invalid-interactive }}
      ...attributes
    >
      {{#if (has-block)}}
        {{!-- When used like <r.cell as |tableMeta|> defining my own cell {{tableMeta.cellIndex}}! </r.cell> --}}
        {{yield this.tableMeta}}
      {{else if (or @cellObject.component @columnObject.cellComponent)}}
        {{! If a component is defined in the cell use it, otherwise check if the column has a cell component name }}
        {{#let (component (or @cellObject.component @columnObject.cellComponent)) as |CellComponent|}}
          <CellComponent
            class={{or @cellObject.componentClassNames @columnObject.cellComponentClassNames}}
            @tableMeta={{this.tableMeta}}
          />
        {{/let}}
      {{else if this.cellValueDefined}}
        {{! The simplest - just define value in your cell and that's what will show }}
        {{formatValue @cellObject.value this.formatEmptyConfig}}
      {{else if @columnObject.valuePath}}
        {{! Find a value with the column's valuePath to a property on the row }}
        {{formatValue (getHelper @rowObject @columnObject.valuePath) this.formatEmptyConfig}}
      {{else}}
        {{! No value or valuePath defined }}
        {{formatValue undefined this.formatEmptyConfig}}
      {{/if}}
    </td>
  </template>
}

export default BasicTableCell
